import styled, { css } from "styled-components";
import { motion } from "framer-motion";
import {
  PageContainer as SharedPageContainer,
  PageState as SharedPageState,
  SectionTitle as SharedSectionTitle,
  TabButton as SharedTabButton,
  Tabs as SharedTabs,
  TableWrapper as SharedTableWrapper,
  getToneTokens,
} from "../../styles/shared";

export const PageState = styled(SharedPageState)``;

export const ClientViewTabs = styled(SharedTabs)`
  border-bottom: 0;
`;

export const ClientViewTab = styled(SharedTabButton)`
  min-height: 36px;
  border: 1px solid ${({ theme, $active }) => ($active ? theme.accent : theme.border)};
  border-radius: 8px;
  background: ${({ theme, $active }) => ($active ? theme.surfaceAlt : theme.surface)};
  margin-bottom: 0;
`;

export const ClientDetailPage = styled(SharedPageContainer)`
  gap: 22px;
`;

export const ClientLinkButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  color: ${({ theme }) => theme.textPrimary};
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  text-align: left;

  &:hover {
    color: ${({ theme }) => theme.accent};
    text-decoration: underline;
  }
`;

export const HealthTableCell = styled.div`
  display: grid;
  gap: 6px;
  min-width: 112px;
`;

export const HealthTableValue = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme, $tone = "default" }) => getToneTokens(theme, $tone).text};
`;

export const HealthTableBarTrack = styled.div`
  width: 100%;
  height: 8px;
  border-radius: 999px;
  background: ${({ theme }) => theme.surfaceAlt};
  border: 1px solid ${({ theme }) => theme.border};
  overflow: hidden;
`;

export const HealthTableBarFill = styled.div`
  height: 100%;
  width: ${({ $width }) => $width};
  border-radius: inherit;
  background: ${({ $color }) => $color};
`;

export const FilterPanel = styled.section`
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 18px;
  background: ${({ theme }) => theme.surface};
  overflow: hidden;
`;

export const FilterPanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 14px 16px;
`;

export const FilterPanelTitleBlock = styled.div`
  display: grid;
  gap: 4px;
`;

export const FilterPanelTitle = styled.h2`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.textSecondary};
`;

export const FilterPanelMeta = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.textSecondary};
`;

export const FilterPanelActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

export const FilterToggleButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 38px;
  padding: 0 14px;
  border-radius: 999px;
  border: 1px solid ${({ theme, $active }) => ($active ? theme.accent : theme.border)};
  background: ${({ theme, $active }) => ($active ? theme.surfaceAlt : theme.surface)};
  color: ${({ theme, $active }) => ($active ? theme.textPrimary : theme.textSecondary)};
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s ease;

  &:hover {
    color: ${({ theme }) => theme.textPrimary};
    border-color: ${({ theme }) => theme.accent};
  }
`;

export const ClearFiltersButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 38px;
  padding: 0 14px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.textSecondary};
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s ease;

  &:hover {
    color: ${({ theme }) => theme.textPrimary};
    border-color: ${({ theme }) => theme.accent};
  }
`;

export const ActiveFilterChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0 16px 14px;
`;

export const ActiveFilterChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 28px;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surfaceAlt};
  color: ${({ theme }) => theme.textSecondary};
  font-size: 12px;
  font-weight: 600;
`;

export const FilterPanelBody = styled(motion.div)`
  overflow: hidden;
`;

export const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 180px), 1fr));
  gap: 12px;
  padding: 0 16px 16px;
`;

export const FilterField = styled.label`
  display: grid;
  gap: 6px;
`;

export const FilterLabel = styled.span`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.textSecondary};
`;

const filterControlCss = css`
  width: 100%;
  min-height: 40px;
  padding: 9px 12px;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surfaceAlt};
  color: ${({ theme }) => theme.textPrimary};
  font: inherit;
  outline: none;
  transition: border-color 0.18s ease, background 0.18s ease;

  &:focus {
    border-color: ${({ theme }) => theme.accent};
    background: ${({ theme }) => theme.surface};
  }
`;

export const FilterInput = styled.input`
  ${filterControlCss}
`;

export const FilterSearchInput = styled(FilterInput)`
  width: min(100%, 260px);
  border-radius: 999px;
`;

export const FilterSelect = styled.select`
  ${filterControlCss}
  cursor: pointer;
`;

export const FilterInlineGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
`;

export const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  padding: 0 14px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.textSecondary};
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.18s ease, color 0.18s ease, background 0.18s ease;

  &:hover {
    color: ${({ theme }) => theme.textPrimary};
    border-color: ${({ theme }) => theme.accent};
    background: ${({ theme }) => theme.surfaceAlt};
  }
`;

export const ClientHeroGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.9fr) minmax(280px, 1fr);
  gap: 16px;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const cardCss = css`
  border-radius: 18px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  box-shadow: 0 16px 34px rgba(15, 15, 20, 0.08);
`;

export const ClientHeroCard = styled.section`
  ${cardCss}
  padding: 22px;
`;

export const ClientTitle = styled.h1`
  font-family: var(--font-display);
  font-size: clamp(1.65rem, 3vw, 2.3rem);
  font-weight: 700;
  color: ${({ theme }) => theme.textPrimary};
`;

export const ClientHeroMeta = styled.div`
  margin-top: 6px;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 14px;
  line-height: 1.5;
`;

export const ClientHeroStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 145px), 1fr));
  gap: 10px;
  margin-top: 18px;
`;

export const ClientHeroStatCard = styled.div`
  border-radius: 14px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surfaceAlt};
  padding: 14px;
`;

export const ClientHeroStatLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.textSecondary};
  margin-bottom: 6px;
`;

export const ClientHeroStatValue = styled.div`
  font-size: clamp(1.1rem, 2.2vw, 1.45rem);
  font-weight: 700;
  color: ${({ theme }) => theme.textPrimary};
`;

export const ClientHealthCard = styled.section`
  ${cardCss}
  padding: 22px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const ClientHealthHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

export const ClientHealthLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.textSecondary};
`;

export const ClientHealthPercentage = styled.div`
  margin-top: 8px;
  font-size: clamp(2rem, 4vw, 2.75rem);
  line-height: 1;
  font-weight: 700;
  color: ${({ theme, $tone = "default" }) => getToneTokens(theme, $tone).text};
`;

export const ClientHealthBarTrack = styled.div`
  width: 100%;
  height: 14px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surfaceAlt};
  overflow: hidden;
`;

export const ClientHealthBarFill = styled(motion.div)`
  height: 100%;
  border-radius: inherit;
  background: ${({ $color }) => $color};
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.12);
`;

export const ClientHealthMeta = styled.div`
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.textSecondary};
`;

export const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 190px), 1fr));
  gap: 12px;
`;

export const InfoCard = styled.div`
  border-radius: 14px;
  padding: 14px;
  background: ${({ theme }) => theme.surfaceAlt};
  border: 1px solid ${({ theme }) => theme.border};
  min-width: 0;
`;

export const InfoLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.textSecondary};
  margin-bottom: 6px;
`;

export const InfoCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

export const CopyIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${({ $compact }) => ($compact ? "20px" : "24px")};
  height: ${({ $compact }) => ($compact ? "20px" : "24px")};
  padding: 0;
  margin-bottom: ${({ $compact }) => ($compact ? "0" : "6px")};
  border-radius: 6px;
  border: 1px solid transparent;
  background: transparent;
  color: ${({ theme }) => theme.textSecondary};
  cursor: pointer;
  flex-shrink: 0;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.surface};
    border-color: ${({ theme }) => theme.border};
    color: ${({ theme }) => theme.textPrimary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 1px;
  }

  ${({ $copied, theme }) =>
    $copied &&
    `
    color: ${theme.successText};
    border-color: ${theme.successBorder};
    background: ${theme.successSurface};
  `}
`;

export const CellCopyWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;

  & > button {
    opacity: 0;
    transition: opacity 0.15s ease;
  }

  tr:hover & > button,
  & > button:focus-visible,
  & > button:hover {
    opacity: 1;
  }

  @media (hover: none) {
    & > button {
      opacity: 1;
    }
  }
`;

export const InfoValue = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.textPrimary};
  overflow-wrap: anywhere;
`;

export const SectionTitle = styled(SharedSectionTitle)`
  margin-bottom: 2px;
`;

export const EmptyState = styled.div`
  padding: 16px 0;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 14px;
`;

export const ObservationList = styled.div`
  display: grid;
  gap: 8px;
`;

export const ObservationItem = styled.div`
  padding: 12px 14px;
  border-radius: 12px;
  background: ${({ theme }) => theme.surfaceAlt};
  border: 1px solid ${({ theme }) => theme.border};
  color: ${({ theme }) => theme.textPrimary};
  font-size: 13px;
  line-height: 1.45;
`;

export const PedidoList = styled.div`
  display: grid;
  gap: 14px;
`;

export const PedidoCard = styled(motion.section)`
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  overflow: hidden;
`;

export const PedidoToggleButton = styled.button`
  width: 100%;
  padding: 18px;
  border: none;
  background: none;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: ${({ theme }) => theme.surfaceAlt};
  }
`;

export const PedidoTitle = styled.h4`
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => theme.textPrimary};
`;

export const PedidoMeta = styled.div`
  margin-top: 4px;
  font-size: 13px;
  color: ${({ theme }) => theme.textSecondary};
  line-height: 1.5;
`;

export const PedidoToggleIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.textSecondary};
  transition: transform 0.24s ease;
  transform: rotate(${({ $open }) => ($open ? "180deg" : "0deg")});
`;

export const PedidoContent = styled(motion.div)`
  overflow: hidden;
`;

export const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 14px 18px 0;
`;

export const Chip = styled.span`
  ${({ theme, $tone = "default" }) => {
    const tokens = getToneTokens(theme, $tone);

    return `
      background: ${tokens.background};
      border: 1px solid ${tokens.border};
      color: ${tokens.text};
    `;
  }}

  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.3;
`;

export const PedidoBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 14px 18px 18px;
`;

export const DetailsTableWrapper = styled(SharedTableWrapper)`
  border-radius: 14px;
`;

export const DetailsTable = styled.table.attrs(({ $density }) => ({
  "data-density": $density === "compact" ? "compact" : undefined,
}))`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  min-width: 1160px;

  &[data-density="compact"] {
    font-size: 11px;
    table-layout: fixed;
    min-width: 0;
  }
`;

export const Th = styled.th`
  /* Bugfix #13 (2026-05-29): sticky header amplo. */
  position: sticky;
  top: var(--app-header-height, 0px);
  z-index: 5;
  padding: 11px 14px;
  text-align: left;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.textSecondary};
  background: ${({ theme }) => theme.surfaceAlt};
  border-bottom: 1px solid ${({ theme }) => theme.border};
  white-space: nowrap;

  [data-density="compact"] & {
    padding: 6px 10px;
    font-size: 10px;
  }
`;

export const Td = styled.td`
  padding: 11px 14px;
  border-bottom: 1px solid ${({ theme }) => theme.border};
  color: ${({ theme, $muted }) => ($muted ? theme.textSecondary : theme.textPrimary)};
  white-space: ${({ $wrap }) => ($wrap ? "normal" : "nowrap")};
  vertical-align: middle;
  line-height: 1.4;

  [data-density="compact"] & {
    padding: 4px 10px;
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
    line-height: 1.25;
  }
`;

/* Entregue + pendencia paralela: os selos lado a lado, quebrando se faltar lugar. */
export const Selos = styled.span`
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  max-width: 100%;
`;

export const StatusBadge = styled.span`
  ${({ theme, $tone = "default" }) => {
    const tokens = getToneTokens(theme, $tone);

    return `
      background: ${tokens.background};
      border: 1px solid ${tokens.border};
      color: ${tokens.text};
    `;
  }}

  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.3;
  white-space: normal;
`;

export const NomeAmbienteButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: ${({ theme }) => theme.textPrimary};
  font-weight: 600;
  cursor: pointer;
  text-align: left;
  transition: color 0.15s ease;
  border-bottom: 1px dashed transparent;

  &:hover,
  &:focus-visible {
    color: ${({ theme }) => theme.accent};
    border-bottom-color: ${({ theme }) => theme.accent};
    outline: none;
  }
`;

/* ============================================================
   HERO CLIENTE — Apple-like
   ============================================================ */

export const HeroContainer = styled.section`
  position: relative;
  border-radius: 22px;
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.border};
  box-shadow: 0 1px 2px rgba(15, 15, 20, 0.04), 0 24px 48px rgba(15, 15, 20, 0.05);
  padding: 28px 32px;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 20px 18px;
    border-radius: 18px;
  }
`;

export const HeroLayout = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 36px;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 22px;
    justify-items: center;
    text-align: center;
  }
`;

export const HeroRingColumn = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export const HeroIdentityColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  min-width: 0;
  width: 100%;
`;

export const HeroClientName = styled.h1`
  font-family: var(--font-display);
  font-size: clamp(1.75rem, 3vw, 2.5rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.textPrimary};
  line-height: 1.1;
  word-break: break-word;
`;

export const HeroIdentityMeta = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 13px;
  font-weight: 500;
  flex-wrap: wrap;
`;

export const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 999px;
  background: ${({ theme }) => theme.surfaceAlt};
  border: 1px solid ${({ theme }) => theme.border};
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.textSecondary};
`;

export const Dot = styled.span`
  color: ${({ theme }) => theme.textSecondary};
  opacity: 0.55;
  font-size: 14px;
  line-height: 1;
`;

export const MiniStatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0;
  margin-top: 4px;

  @media (max-width: 540px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;

export const MiniStat = styled.div`
  position: relative;
  padding: 0 18px;
  display: flex;
  flex-direction: column;
  gap: 4px;

  &:first-child {
    padding-left: 0;
  }

  & + & {
    border-left: 1px solid ${({ theme }) => theme.border};
  }

  @media (max-width: 540px) {
    padding: 8px 0;
    & + & {
      border-left: none;
      border-top: 1px solid ${({ theme }) => theme.border};
    }
    &:first-child {
      padding-top: 0;
    }
  }
`;

const miniStatToneColor = ({ theme, $tone = "default" }) => {
  if ($tone === "danger") return theme.dangerText;
  if ($tone === "success") return theme.successText;
  if ($tone === "warning") return theme.warningText;
  return theme.textPrimary;
};

export const MiniStatValue = styled.div`
  font-size: clamp(1.35rem, 2.4vw, 1.75rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.1;
  color: ${miniStatToneColor};
`;

export const MiniStatLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.textSecondary};
  font-weight: 500;
`;

/* ============================================================
   ACTIVITY RING (Saúde do cliente)
   ============================================================ */

export const RingWrapper = styled.div`
  position: relative;
  width: 168px;
  height: 168px;
  color: ${({ theme }) => theme.textSecondary};

  @media (max-width: 768px) {
    width: 148px;
    height: 148px;
  }
`;

export const RingSvg = styled.svg`
  width: 100%;
  height: 100%;
  display: block;
`;

export const RingHalo = styled(motion.span)`
  position: absolute;
  inset: -6px;
  border-radius: 50%;
  background: radial-gradient(closest-side, rgba(52, 199, 89, 0.28), transparent 70%);
  pointer-events: none;
  z-index: 0;
`;

export const RingCenter = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  z-index: 2;
  pointer-events: none;
`;

const ringTextColor = ({ theme, $tone = "default" }) => {
  if ($tone === "success") return theme.successText;
  if ($tone === "warning") return theme.warningText;
  if ($tone === "danger") return theme.dangerText;
  if ($tone === "today") return theme.todayText;
  return theme.textPrimary;
};

export const RingPercentage = styled.div`
  font-size: clamp(2rem, 4vw, 2.6rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1;
  color: ${ringTextColor};
`;

export const RingStatus = styled.div`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: ${({ theme }) => theme.textSecondary};
`;

/* ============================================================
   STAT LIST — listas densas estilo iOS Settings
   ============================================================ */

export const StatListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 22px;
`;

export const StatGroup = styled.section`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const StatGroupTitle = styled.h3`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.textSecondary};
  padding-left: 4px;
  margin-bottom: 2px;
`;

export const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(${({ $columns = 1 }) => $columns}, minmax(0, 1fr));
  gap: 0 24px;
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 14px;
  overflow: hidden;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

export const StatRow = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, auto) minmax(0, 1fr) auto;
  align-items: center;
  gap: 16px;
  padding: 11px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.border};
  transition: background 0.18s ease;

  &:last-child,
  &:nth-last-child(2):nth-child(odd) {
    border-bottom: none;
  }

  ${({ $columns }) =>
    $columns === 2 &&
    css`
      &:nth-child(even) {
        border-left: 1px solid ${({ theme }) => theme.border};
      }
    `}

  &:hover {
    background: var(--surface-hover);
  }

  &:hover [data-copy-slot] {
    opacity: 1;
  }
`;

export const StatLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.textSecondary};
  white-space: nowrap;
`;

const statValueTone = ({ theme, $tone = "default" }) => {
  if ($tone === "danger") return theme.dangerText;
  if ($tone === "success") return theme.successText;
  if ($tone === "warning") return theme.warningText;
  return theme.textPrimary;
};

export const StatValue = styled.div`
  font-size: 13.5px;
  font-weight: 600;
  color: ${statValueTone};
  text-align: right;
  word-break: break-word;
  letter-spacing: -0.01em;
`;

export const StatCopySlot = styled.div.attrs({ "data-copy-slot": true })`
  opacity: 0;
  transition: opacity 0.18s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;

  @media (hover: none) {
    opacity: 1;
  }
`;

/* ============================================================
   OBSERVAÇÕES — chip + texto
   ============================================================ */

export const ObservacaoListNew = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ObservacaoCardWrapper = styled.article`
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: start;
  gap: 14px;
  padding: 12px 16px;
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 12px;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba(15, 15, 20, 0.06);
    border-color: ${({ theme }) => theme.accent}40;
  }
`;

export const ObservacaoChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  background: ${({ theme }) => theme.surfaceAlt};
  border: 1px solid ${({ theme }) => theme.border};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.textSecondary};
  white-space: nowrap;
  flex-shrink: 0;
`;

export const ObservacaoText = styled.p`
  font-size: 13px;
  line-height: 1.55;
  color: ${({ theme }) => theme.textPrimary};
  margin: 0;
  padding-top: 2px;
`;
